import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  adminOnly?: boolean;
  minRole?: 'user' | 'eskimi_admin' | 'adops' | 'admin';
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  adminOnly = false,
  minRole = 'user'
}) => {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-canvas flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center">
            <img
              src="/eskimi-logo-medium.png"
              alt="Eskimi Logo"
              className="h-12 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-2xl font-bold text-ink">
            Loading...
          </h2>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-surface py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
              <p className="text-sm text-center text-ink-muted">
                Checking authentication...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // When impersonating, the user object contains the impersonated user with their actual role
  const userRole = user?.role || 'user';

  // Check role-based access
  const hasAccess = () => {
    if (adminOnly || minRole === 'admin') {
      return userRole === 'admin';
    }
    if (minRole === 'eskimi_admin' || minRole === 'adops') {
      return userRole === 'eskimi_admin' || userRole === 'adops' || userRole === 'admin';
    }
    return true; // minRole === 'user' is accessible to everyone
  };

  if (!hasAccess()) {
    return <Navigate to="/explore" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;